// =============================================================================
// useFangateVariants — group Fangate "variant" clones into single logical
// products via union-find over two empirically-verified signals.
//
// Fangate's POST /products/{id}/price-links creates a NEW PRODUCT (with a new
// id and disjoint media-row ids). The underlying S3 file is shared, so
// `media[].source` exposes a signed URL whose PATH portion (before the `?`)
// is stable across clones.
//
// Two grouping signals:
//
//   1. Media storage-path overlap.
//      Strip the signed query-string off each media's `source` (or fall back
//      to `preview`) and use the path as a fingerprint. Products sharing any
//      fingerprint = same underlying content = clones of each other.
//      Empirically verified on production (6 cloned "Test" products all
//      exposed the same `fangate-prod/.../15931135171779953649.png`).
//
//   2. localStorage variant-link map (optional second channel).
//      Recorded whenever the user creates a clone via our UI. Covers the
//      hypothetical case where Fangate ever changes its URL scheme and
//      signal #1 breaks, and works the moment after creation (before the
//      next listProducts can refresh the in-memory product set).
//
// Both signals feed the same union-find pass. The lowest product id in each
// cluster is the "primary" (stable across reloads — no L-pill jitter).
//
// API:
//
//   groupProductsByMedia(products, { variantMap = {} } = {})
//     → Array<{ primary: Product, variants: Product[] }>
//   Each result entry's `variants` includes the primary at index 0; clusters
//   of size 1 still come back as { primary, variants: [primary] }.
//
//   mediaPathOf(mediaObj)       → string|null (the stable path part)
//   fingerprintOf(product)      → string (sorted, '|'-joined paths)
//
// Pure functions — no Vue reactivity, no FangateAPI imports. Caller decides
// how to render the clusters (the side panel collapses into a single card
// with an L-pill selector; the vault shows one card with a "×N" badge).
// =============================================================================

const stripQuery   = u => (typeof u === 'string' ? u.split('?')[0] : null);
const mediaPathOf  = m => stripQuery(m?.source) || stripQuery(m?.preview);

const fingerprintOf = product => (product?.media || [])
  .map(mediaPathOf)
  .filter(Boolean)
  .sort()
  .join('|');

const groupProductsByMedia = (products, { variantMap = {} } = {}) => {
  const items = Array.isArray(products) ? products : [];
  if (items.length === 0) return [];

  const idxOf  = new Map(items.map((p, i) => [String(p?.id), i]));
  const parent = items.map((_, i) => i);
  const find   = i => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const union  = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };

  // Signal 1 — media-path overlap.
  const byFp = new Map();
  items.forEach((p, i) => {
    const fp = fingerprintOf(p);
    if (!fp) return;
    if (byFp.has(fp)) union(byFp.get(fp), i);
    else byFp.set(fp, i);
  });

  // Signal 2 — variant-link map (e.g. localStorage source→clone records).
  Object.entries(variantMap).forEach(([sourceId, variantIds]) => {
    const si = idxOf.get(String(sourceId));
    if (si == null) return;
    (Array.isArray(variantIds) ? variantIds : []).forEach(vId => {
      const vi = idxOf.get(String(vId));
      if (vi != null) union(si, vi);
    });
  });

  // Bucket by root.
  const buckets = new Map();
  items.forEach((p, i) => {
    const root = find(i);
    if (!buckets.has(root)) buckets.set(root, []);
    buckets.get(root).push(p);
  });

  // Sort each bucket by numeric id ascending → primary = oldest product.
  return [...buckets.values()].map(group => {
    group.sort((a, b) => Number(a?.id) - Number(b?.id));
    return { primary: group[0], variants: group };
  });
};

export { mediaPathOf, fingerprintOf, groupProductsByMedia };
export default { mediaPathOf, fingerprintOf, groupProductsByMedia };
